const TARGET_POSITIONS = ["PG", "SG", "SF", "PF", "C"] as const;
type TargetPos = (typeof TARGET_POSITIONS)[number];

const getPlayerRatings = (p: any) =>
	Array.isArray(p.ratings)
		? p.ratings.at(-1) || p.ratings[0] || {}
		: p.ratings || {};

const getPlayerPos = (p: any): string => {
	const r = getPlayerRatings(p);
	return r.pos || p.pos || "SF";
};

const getPlayerSkills = (p: any): string[] => {
	const r = getPlayerRatings(p);
	return r.skills || p.skills || [];
};

/** Assigns the five displayed lineup players to the best-fitting basketball slots. */
const calculatePositionFitScore = (p: any, target: TargetPos): number => {
	const pos = getPlayerPos(p);
	const r = getPlayerRatings(p);
	const skills = getPlayerSkills(p);

	let posScore = 0;
	switch (pos) {
		case "PG":
			posScore =
				target === "PG"
					? 100
					: target === "SG"
						? 65
						: target === "SF"
							? 20
							: target === "PF"
								? -20
								: -100;
			break;
		case "SG":
			posScore =
				target === "SG"
					? 100
					: target === "PG"
						? 60
						: target === "SF"
							? 70
							: target === "PF"
								? 10
								: -100;
			break;
		case "G":
			posScore =
				target === "PG" || target === "SG"
					? 85
					: target === "SF"
						? 30
						: target === "PF"
							? 0
							: -50;
			break;
		case "GF":
			posScore =
				target === "SG" || target === "SF"
					? 85
					: target === "PG"
						? 40
						: target === "PF"
							? 30
							: -50;
			break;
		case "SF":
			posScore =
				target === "SF"
					? 100
					: target === "SG"
						? 65
						: target === "PF"
							? 70
							: target === "PG"
								? 15
								: 10;
			break;
		case "F":
			posScore =
				target === "SF" || target === "PF"
					? 85
					: target === "SG"
						? 40
						: target === "C"
							? 40
							: -50;
			break;
		case "PF":
			posScore =
				target === "PF"
					? 100
					: target === "C"
						? 75
						: target === "SF"
							? 60
							: target === "SG"
								? -20
								: -100;
			break;
		case "FC":
			posScore =
				target === "PF" || target === "C"
					? 85
					: target === "SF"
						? 40
						: target === "SG"
							? -20
							: -50;
			break;
		case "C":
			posScore =
				target === "C"
					? 100
					: target === "PF"
						? 75
						: target === "SF"
							? 10
							: target === "SG"
								? -50
								: -100;
			break;
		default:
			posScore = 30;
	}

	const pss = r.pss ?? 50;
	const drb = r.drb ?? 50;
	const spd = r.spd ?? 50;
	const tp = r.tp ?? 50;
	const fg = r.fg ?? 50;
	const reb = r.reb ?? 50;
	const stre = r.stre ?? 50;
	const hgt = p.hgt ?? r.hgt ?? 50;
	const ins = r.ins ?? 50;
	const diq = r.diq ?? 50;

	let attrScore = 0;
	if (target === "PG") {
		attrScore = 0.2 * pss + 0.15 * drb + 0.1 * spd;
		if (skills.includes("Ps")) {attrScore += 10;}
		if (skills.includes("B")) {attrScore += 5;}
	} else if (target === "SG") {
		attrScore = 0.25 * tp + 0.1 * fg + 0.1 * spd;
		if (skills.includes("3")) {attrScore += 10;}
		if (skills.includes("A")) {attrScore += 5;}
	} else if (target === "SF") {
		attrScore = 0.15 * spd + 0.1 * tp + 0.1 * hgt + 0.1 * drb;
		if (skills.includes("3")) {attrScore += 5;}
		if (skills.includes("A")) {attrScore += 5;}
		if (skills.includes("Dp")) {attrScore += 5;}
	} else if (target === "PF") {
		attrScore = 0.2 * reb + 0.15 * stre + 0.1 * hgt + 0.05 * ins;
		if (skills.includes("R")) {attrScore += 8;}
		if (skills.includes("Po")) {attrScore += 7;}
		if (skills.includes("Di")) {attrScore += 5;}
	} else {
		attrScore = 0.25 * reb + 0.2 * stre + 0.15 * hgt + 0.1 * diq;
		if (skills.includes("R")) {attrScore += 10;}
		if (skills.includes("Po")) {attrScore += 10;}
		if (skills.includes("Di")) {attrScore += 5;}
	}

	return posScore + attrScore;
};

const permutations: number[][] = [];
const generatePermutations = (arr: number[], prefix: number[] = []) => {
	if (arr.length === 0) {
		permutations.push(prefix);
		return;
	}
	for (let i = 0; i < arr.length; i++) {
		const rest = [...arr];
		const [current] = rest.splice(i, 1);
		generatePermutations(rest, [...prefix, current!]);
	}
};
generatePermutations([0, 1, 2, 3, 4]);

const assignPositionsSmart = (players: any[]): Record<string, any> => {
	const assigned: Record<string, any> = {};
	if (!players?.length) {return assigned;}

	if (players.length < 5) {
		const unassigned = [...players];
		for (
			let i = 0;
			i < Math.min(unassigned.length, TARGET_POSITIONS.length);
			i++
		) {
			const target = TARGET_POSITIONS[i]!;
			unassigned.sort(
				(a, b) =>
					calculatePositionFitScore(b, target) -
					calculatePositionFitScore(a, target),
			);
			assigned[target] = unassigned.shift();
		}
		return assigned;
	}

	const top5 = players.slice(0, 5);
	const scoreMatrix = top5.map((player) =>
		TARGET_POSITIONS.map((target) => calculatePositionFitScore(player, target)),
	);
	let maxScore = -Infinity;
	let bestPermutation = permutations[0]!;
	for (const permutation of permutations) {
		const score = permutation.reduce(
			(total, playerIndex, positionIndex) =>
				total + scoreMatrix[playerIndex]![positionIndex]!,
			0,
		);
		if (score > maxScore) {
			maxScore = score;
			bestPermutation = permutation;
		}
	}

	for (let i = 0; i < TARGET_POSITIONS.length; i++) {
		assigned[TARGET_POSITIONS[i]!] = top5[bestPermutation[i]!];
	}
	return assigned;
};

export default assignPositionsSmart;
